import { IFieldMapping, IQrBarcodeModel, IQrPayload, IQrReportObject } from '../models/qr-label.model';

/**
 * Utility functions for QR Code payload encoding, decoding, field resolution and mapping.
 */
export class QrPayloadEncoderUtil {
  /**
   * Resolves a field value from an item using direct match, title match, or normalized alphanumeric key comparison.
   */
  public static resolveFieldValue(item: any, fieldKey: string, fieldTitle?: string, fallback: string = ''): string {
    if (!item) return fallback;
    const cleanKey = (fieldKey || '').replace('(QRCode image)', '').replace('(barcode image)', '').trim();
    const cleanTitle = (fieldTitle || '').replace('(QRCode image)', '').replace('(barcode image)', '').trim();

    // 1. Direct match by cleanKey
    if (cleanKey && item[cleanKey] !== undefined && item[cleanKey] !== null && String(item[cleanKey]).trim() !== '') {
      return String(item[cleanKey]).trim();
    }

    // 2. Direct match by cleanTitle
    if (cleanTitle && item[cleanTitle] !== undefined && item[cleanTitle] !== null && String(item[cleanTitle]).trim() !== '') {
      return String(item[cleanTitle]).trim();
    }

    // 3. Normalized alphanumeric matching (case-insensitive & stripped spaces/punctuation)
    const norm = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const normKey = norm(cleanKey);
    const normTitle = norm(cleanTitle);

    for (const k of Object.keys(item)) {
      const nk = norm(k);
      if ((normKey && nk === normKey) || (normTitle && nk === normTitle)) {
        if (item[k] !== undefined && item[k] !== null && String(item[k]).trim() !== '') {
          return String(item[k]).trim();
        }
      }
    }

    // 4. Common identifier fallbacks if field represents an ID / Barcode
    if (normKey.includes('id') || normKey.includes('barcode') || normKey.includes('code') || normKey.includes('number')) {
      const fallbackVal = item.BarcodeNumber ?? item.barcodeNumber ?? item.Barcode ?? item.barcode ?? item.code ?? item.id ?? item.AssetId ?? item.assetId;
      if (fallbackVal !== undefined && fallbackVal !== null && String(fallbackVal).trim() !== '') {
        return String(fallbackVal).trim();
      }
    }

    // 5. Common description fallbacks
    if (normKey.includes('desc') || normKey.includes('name') || normKey.includes('title')) {
      const fallbackVal = item.displayName ?? item.DisplayName ?? item.Description ?? item.description ?? item.Name ?? item.name;
      if (fallbackVal !== undefined && fallbackVal !== null && String(fallbackVal).trim() !== '') {
        return String(fallbackVal).trim();
      }
    }

    return fallback;
  }

  /**
   * Safely encodes a string / JSON payload to Base64 supporting UTF-8 characters.
   */
  public static toBase64Utf8(text: string): string {
    try {
      const bytes = new TextEncoder().encode(text);
      let binary = '';
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      return btoa(binary);
    } catch {
      try {
        return btoa(unescape(encodeURIComponent(text)));
      } catch {
        return btoa(text);
      }
    }
  }

  /**
   * Safely decodes a Base64 string supporting UTF-8 characters.
   */
  public static fromBase64Utf8(base64: string): string {
    try {
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return new TextDecoder().decode(bytes);
    } catch {
      try {
        return decodeURIComponent(escape(atob(base64)));
      } catch {
        return atob(base64);
      }
    }
  }

  /**
   * Builds the QR target URL containing base64 encoded parameters.
   */
  public static buildQrTargetUrl(
    payload: IQrPayload,
    baseOrigin: string = window.location.origin
  ): string {
    const jsonStr = JSON.stringify(payload);
    const encoded = this.toBase64Utf8(jsonStr);

    const queryParams: string[] = [`qrcode=${encodeURIComponent(encoded)}`];
    if (payload.labelId) queryParams.push(`labelId=${encodeURIComponent(payload.labelId)}`);
    if (payload.catId) queryParams.push(`cat=${encodeURIComponent(payload.catId)}`);
    if (payload.filterBy) queryParams.push(`field=${encodeURIComponent(payload.filterBy)}`);
    if (payload.val) queryParams.push(`val=${encodeURIComponent(payload.val)}`);
    if (payload.name) queryParams.push(`name=${encodeURIComponent(payload.name)}`);

    return `${baseOrigin}/decode-qr?${queryParams.join('&')}`;
  }

  /**
   * Extracts and normalizes selected fields configuration.
   */
  public static extractFieldMappings(reportObject: IQrReportObject, defaultFieldKey: string = ''): IFieldMapping[] {
    let rawSelectedFields = reportObject?.selectedFields || [];
    if (typeof rawSelectedFields === 'string') {
      rawSelectedFields = (rawSelectedFields as string).split(',').filter(s => s.trim().length > 0).map(s => ({ field: s.trim(), title: s.trim() }));
    } else if (rawSelectedFields.length === 0 && reportObject?.ReportObj?.selectedFields) {
      const rawStr = reportObject.ReportObj.selectedFields;
      if (typeof rawStr === 'string') {
        rawSelectedFields = rawStr.split(',').filter(s => s.trim().length > 0).map(s => ({ field: s.trim(), title: s.trim() }));
      }
    }

    const allFieldObjs: IFieldMapping[] = [];
    const seenFields = new Set<string>();

    rawSelectedFields.forEach((f: any) => {
      const fieldStr = (typeof f === 'string' ? f : f.field || f.title || '').trim();
      const titleStr = (typeof f === 'string' ? f : f.title || f.field || '').trim();
      const isBarcodeImage = titleStr.includes('(QRCode image)') || titleStr.includes('(barcode image)') || fieldStr.includes('(QRCode image)') || fieldStr.includes('(barcode image)');
      const cleanField = fieldStr.replace('(QRCode image)', '').replace('(barcode image)', '').trim();
      const cleanTitle = titleStr.replace('(QRCode image)', '').replace('(barcode image)', '').trim();

      if (cleanField && !seenFields.has(cleanField.toLowerCase())) {
        seenFields.add(cleanField.toLowerCase());
        allFieldObjs.push({ field: cleanField, title: cleanTitle || cleanField, isQrOnly: isBarcodeImage });
      }
    });

    if (allFieldObjs.length === 0 && defaultFieldKey) {
      allFieldObjs.push({ field: defaultFieldKey, title: defaultFieldKey, isQrOnly: false });
    }

    return allFieldObjs;
  }
}
